import React, { useState } from "react";
import {
  Box,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import { alpha } from "@mui/material/styles";
import Flx from "../../../components/layout/Flx";
import colors from "../../../theme/colors";
import T from "../submissionTableTokens";
import {
  GROUP_SORT_OPTIONS,
  LANES,
  LANE_META,
  LANE_PILL_LABELS,
  SCOPE_OPTIONS,
  initialsOf,
} from "../submissionHelpers";

const PILL_LANES = [
  LANES.ALL,
  LANES.LOAN_APPLICATION,
  LANES.CREDIT_AUTHORIZATION,
];

/**
 * A filter pill with its count. Selecting one navigates — the type filter lives
 * in the URL so the Submissions nav dropdown deep-links into it and a reload
 * keeps it. The two toggles beside them are view state and deliberately don't.
 */
const Pill = ({ label, count, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      cursor: active ? "default" : "pointer",
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 550,
      lineHeight: 1,
      px: "14px",
      py: "8px",
      borderRadius: "20px",
      transition: "background .15s, border-color .15s, color .15s",
      color: active ? "primary.dark" : T.ink500,
      background: (t) =>
        active ? alpha(t.palette.primary.main, 0.08) : "#fff",
      border: (t) =>
        `1px solid ${active ? t.palette.primary.main : colors.slate200}`,
      "&:hover": { borderColor: active ? undefined : colors.slate300 },
    }}
  >
    {label}
    {count != null && (
      <Box
        component="span"
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: active ? "primary.main" : T.ink300,
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

/**
 * A label-and-track switch. MUI's Switch carries its own sizing and ripple that
 * fights the compact toolbar, so this is the plain two-element version — a track
 * and a knob, both driven off `on`.
 */
const Toggle = ({ on, onChange, children }) => (
  <Flx
    centerY
    gap={1}
    onClick={onChange}
    sx={{
      cursor: "pointer",
      userSelect: "none",
      fontSize: 12.5,
      fontWeight: 550,
      color: on ? "primary.dark" : T.ink500,
      transition: "color .15s",
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 20,
        borderRadius: "12px",
        position: "relative",
        flexShrink: 0,
        background: (t) => (on ? t.palette.primary.main : "#D6DEE9"),
        transition: "background .2s",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 2,
          left: on ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(16,30,51,.25)",
          transition: "left .2s",
        },
      }}
    />
    {children}
  </Flx>
);

/** Monogram avatar for the logged-in user, dimmed until "My submissions" is on. */
const MeAvatar = ({ name, active, size = 19 }) => (
  <Flx
    centerX
    centerY
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      background: "linear-gradient(135deg,#1E3A66,#0F1E33)",
      color: "#fff",
      fontSize: size <= 20 ? 8 : 10,
      fontWeight: 600,
      opacity: active ? 1 : 0.55,
      filter: active ? "none" : "grayscale(.4)",
      transition: "opacity .2s, filter .2s",
    }}
  >
    {initialsOf(name)}
  </Flx>
);

/**
 * A labelled dropdown: current selection on a quiet button, options with their
 * hint text in a menu. Used for both the scope filter and the group ordering —
 * they're the same control, so they're one component.
 */
const DropdownSelect = ({ value, options, onChange, icon }) => {
  const [anchor, setAnchor] = useState(null);
  const active = options.find((o) => o.value === value) || options[0];

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 550,
          color: colors.slate900,
          background: "#fff",
          border: `1px solid ${colors.slate200}`,
          borderRadius: "9px",
          px: "13px",
          py: "8px",
          transition: "border-color .15s",
          "&:hover": { borderColor: colors.slate300 },
        }}
      >
        {icon}
        {active.label}
        <KeyboardArrowDownRoundedIcon
          sx={{ fontSize: 16, color: colors.slate400 }}
        />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 230, borderRadius: "10px" } } }}
      >
        {options.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              onChange(o.value);
              setAnchor(null);
            }}
            sx={{ borderRadius: "7px", mx: 0.5 }}
          >
            <ListItemText
              primary={o.label}
              secondary={o.hint}
              primaryTypographyProps={{ fontSize: 13 }}
              secondaryTypographyProps={{ fontSize: 11.5 }}
            />
            <CheckRoundedIcon
              sx={{
                fontSize: 16,
                ml: 1.5,
                color: "primary.main",
                opacity: o.value === value ? 1 : 0,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SubmissionsToolbar = ({
  lane,
  onLaneChange,
  counts,
  query,
  onQueryChange,
  mineOnly,
  onMineOnlyChange,
  grouped,
  onGroupedChange,
  groupSort,
  onGroupSortChange,
  scope,
  onScopeChange,
  userName,
  canFilterMine,
}) => {
  return (
    <Flx centerY wrap gap={1.25}>
      <TextField
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search borrower, email, property, loan"
        size="small"
        sx={{ width: { xs: "100%", sm: 300 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: colors.slate400 }} />
            </InputAdornment>
          ),
        }}
      />

      <Flx centerY gap={0.75}>
        {PILL_LANES.map((key) => (
          <Pill
            key={key}
            label={LANE_PILL_LABELS[key]}
            count={counts[key]}
            active={lane === key}
            onClick={() => lane !== key && onLaneChange(LANE_META[key].path)}
          />
        ))}
      </Flx>

      <Box sx={{ flex: 1 }} />

      {/* Hidden rather than disabled when attribution is unavailable — a toggle
          that can only ever return nothing is worse than no toggle. */}
      {canFilterMine && (
        <Toggle on={mineOnly} onChange={() => onMineOnlyChange(!mineOnly)}>
          <Flx centerY gap={0.875}>
            <MeAvatar name={userName} active={mineOnly} />
            My submissions
          </Flx>
        </Toggle>
      )}

      <Toggle on={grouped} onChange={() => onGroupedChange(!grouped)}>
        Group by borrower
      </Toggle>

      {/* Only while grouped: the column headers can't sort in that mode (the
          group rows would scatter), so this is where the order is chosen. */}
      {grouped && (
        <DropdownSelect
          value={groupSort}
          options={GROUP_SORT_OPTIONS}
          onChange={onGroupSortChange}
          icon={
            <SwapVertRoundedIcon
              sx={{ fontSize: 16, color: colors.slate400 }}
            />
          }
        />
      )}

      <DropdownSelect
        value={scope}
        options={SCOPE_OPTIONS}
        onChange={onScopeChange}
      />
    </Flx>
  );
};

export { MeAvatar };
export default SubmissionsToolbar;
